import { useCallback, useEffect } from 'react';
import { FieldValues, FormState } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';

import { Button, Modal } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';

interface Props {
  formState: FormState<FieldValues>;
}
export function FormLeaveBlocker(props: Props) {
  const { formState } = props;
  const { t } = useTranslation();
  const shouldBlock = formState.isDirty && !formState.isSubmitted && !formState.isSubmitting;
  const blocker = useBlocker(shouldBlock); // Block from in-app navigation

  const onUnload = useCallback((e: Event) => {
    e.preventDefault();
    e.returnValue = true; // compat
  }, []);

  // Block from closing the window
  useEffect(() => {
    if (shouldBlock === false) {
      return;
    }

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [shouldBlock, onUnload]);

  // Reset the blocker when shouldBlock is set to false
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }
    if (shouldBlock === false) {
      blocker.reset();
    }
  }, [blocker, shouldBlock]);

  function handlePromptClose() {
    if (blocker.state !== 'blocked') {
      return;
    }
    blocker.reset();
  }

  function handlePromptProceed() {
    if (blocker.state !== 'blocked') {
      return;
    }
    setTimeout(() => blocker.proceed(), 0);
  }

  return (
    <Modal.Root onOpenChange={handlePromptClose} open={blocker.state === 'blocked'}>
      <Modal.Content>
        <Modal.Header title={t('common.leavePageTitle')} />

        <Modal.Body>{t('common.leavePageDescription')}</Modal.Body>

        <Modal.Footer>
          <Modal.CloseButton>
            <Button variant="secondary">{t('common.cancel')}</Button>
          </Modal.CloseButton>

          <Button onClick={handlePromptProceed}>{t('common.leavePage')}</Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
